import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileSpreadsheet,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  ShoppingCart,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { toast } from "~/hooks/use-toast";
import { useUnreadContactAttempts } from "~/hooks/useUnreadContactAttempts";
import { supabase } from "~/integrations/supabase/client";
import { Database } from "~/integrations/supabase/types";

import { CotizacionSourceBadge } from "./CotizacionSourceBadge";
import { exportCotizacionesToExcel } from "./utils/exportCotizaciones";

type ContactAttempt = Database["public"]["Tables"]["contact_attempts"]["Row"];

const ITEMS_PER_PAGE = 10;

const CotizacionesPage = () => {
  const navigate = useNavigate();
  const [cotizaciones, setCotizaciones] = useState<ContactAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const { refetch: refetchUnreadCount } = useUnreadContactAttempts();

  const fetchCotizaciones = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("contact_attempts")
        .select("*")
        .order("created_at", { ascending: false });

      if (typeFilter !== "all") {
        const isQuotation = typeFilter === "quotation";
        query = query.eq("is_quotation", isQuotation);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setCotizaciones(data || []);
    } catch (error) {
      console.error("Error fetching cotizaciones:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las cotizaciones",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCotizaciones();
  }, [typeFilter]);

  // Refetch unread count when component mounts to ensure sidebar is up to date
  useEffect(() => {
    refetchUnreadCount();
  }, [refetchUnreadCount]);

  const filteredCotizaciones = cotizaciones.filter((cotizacion) => {
    if (sourceFilter !== "all") {
      const source = cotizacion.source === "nexor" ? "nexor" : "website";
      if (source !== sourceFilter) return false;
    }

    const searchLower = searchTerm.toLowerCase();
    return (
      cotizacion.full_name.toLowerCase().includes(searchLower) ||
      cotizacion.email.toLowerCase().includes(searchLower) ||
      cotizacion.company.toLowerCase().includes(searchLower) ||
      cotizacion.location.toLowerCase().includes(searchLower) ||
      cotizacion.message.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCotizaciones.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCotizaciones = filteredCotizaciones.slice(
    startIndex,
    endIndex
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter, sourceFilter]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleViewDetails = (cotizacionId: string) => {
    navigate(`/cotizaciones/${cotizacionId}`);
  };

  const handleExport = () => {
    try {
      const filename = exportCotizacionesToExcel(filteredCotizaciones);
      toast({
        title: "Excel exportado",
        description: `Se descargó ${filename} con ${filteredCotizaciones.length} registros.`,
      });
    } catch (error) {
      console.error("Error exporting cotizaciones:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo Excel.",
        variant: "destructive",
      });
    }
  };

  const getCartItemsCount = (cartItems: any) => {
    if (!cartItems || typeof cartItems !== "object") return 0;
    if (Array.isArray(cartItems)) return cartItems.length;
    if (cartItems.items && Array.isArray(cartItems.items))
      return cartItems.items.length;
    return 0;
  };

  if (loading) {
    return (
      <div className="w-[80vw] space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-24" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-full sm:w-48" />
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Skeleton className="h-6 w-8 rounded-full" />
                  <Skeleton className="h-4 w-40" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Cotizaciones List */}
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                  <Skeleton className="h-12 w-full rounded-md" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[80vw] space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Cotizaciones</h1>
          <p className="text-muted-foreground">
            Gestiona las solicitudes de cotización y consultas de clientes
            potenciales
          </p>
        </div>
        <Button
          variant="outline"
          className="h-9"
          onClick={handleExport}
          disabled={filteredCotizaciones.length === 0}
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nombre, email, empresa, ubicación o mensaje..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="quotation">Solo Cotizaciones</SelectItem>
                  <SelectItem value="inquiry">Solo Consultas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-48">
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Fuente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fuentes</SelectItem>
                  <SelectItem value="website">Sitio Web</SelectItem>
                  <SelectItem value="nexor">Agente Nexor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Badge className="border-orange-200 bg-orange-100 text-orange-800">
                {cotizaciones.filter((c) => c.is_quotation === true).length}
              </Badge>
              <span className="text-sm font-medium">
                Solicitudes de Cotización
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Badge className="border-cyan-200 bg-cyan-100 text-cyan-800">
                {
                  cotizaciones.filter(
                    (c) => c.is_quotation === false || c.is_quotation === null
                  ).length
                }
              </Badge>
              <span className="text-sm font-medium">Consultas Generales</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Badge className="border-violet-200 bg-violet-100 text-violet-800">
                {cotizaciones.filter((c) => c.source === "nexor").length}
              </Badge>
              <span className="text-sm font-medium">Generadas por Nexor</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cotizaciones List */}
      <div className="space-y-4">
        {filteredCotizaciones.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <MessageSquare className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                No hay cotizaciones
              </h3>
              <p className="text-muted-foreground">
                {searchTerm || typeFilter !== "all" || sourceFilter !== "all"
                  ? "No se encontraron cotizaciones que coincidan con los filtros."
                  : "Aún no hay solicitudes de cotización."}
              </p>
            </CardContent>
          </Card>
        ) : (
          paginatedCotizaciones.map((cotizacion, index) => (
            <Card
              key={cotizacion.id}
              style={{ animationDelay: `${Math.min(index * 30, 360)}ms` }}
              className={`cursor-pointer transition-shadow duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:shadow-md ${
                !cotizacion.is_read
                  ? "border-l-4 border-l-orange-500 bg-orange-50/30"
                  : ""
              }`}
              onClick={() => handleViewDetails(cotizacion.id)}
            >
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    {!cotizacion.is_read && (
                      <div className="h-2 w-2 rounded-full bg-orange-500"></div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3
                          className={`text-lg ${!cotizacion.is_read ? "font-bold" : "font-semibold"}`}
                        >
                          {cotizacion.full_name}
                        </h3>
                        {cotizacion.is_quotation && (
                          <Badge className="border-orange-200 bg-orange-100 text-orange-800">
                            <ShoppingCart className="mr-1 h-3 w-3" />
                            Cotización
                          </Badge>
                        )}
                        <CotizacionSourceBadge source={cotizacion.source} />
                        {!cotizacion.is_read && (
                          <Badge className="border-blue-200 bg-blue-100 text-blue-800">
                            Nuevo
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Building2 className="mr-1 h-4 w-4" />
                        {cotizacion.company}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Mail className="mr-1 h-4 w-4" />
                      {cotizacion.email}
                    </div>
                    <div className="flex items-center">
                      <Phone className="mr-1 h-4 w-4" />
                      {cotizacion.phone}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="mr-1 h-4 w-4" />
                      {cotizacion.location}
                    </div>
                    <div className="flex items-center">
                      <Clock className="mr-1 h-4 w-4" />
                      {formatDate(cotizacion.created_at)}
                    </div>
                    {cotizacion.cart_items &&
                      getCartItemsCount(cotizacion.cart_items) > 0 && (
                        <div className="flex items-center">
                          <ShoppingCart className="mr-1 h-4 w-4" />
                          {getCartItemsCount(cotizacion.cart_items)} productos
                        </div>
                      )}
                  </div>

                  <div className="rounded-md bg-muted/50 p-3">
                    <p className="line-clamp-2 text-sm">{cotizacion.message}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredCotizaciones.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}-
            {Math.min(endIndex, filteredCotizaciones.length)} de{" "}
            {filteredCotizaciones.length} resultados
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - currentPage) <= 1) return true;
                  return false;
                })
                .map((page, index, array) => {
                  const showEllipsis = index > 0 && page - array[index - 1] > 1;
                  return (
                    <div key={page} className="flex items-center">
                      {showEllipsis && (
                        <span className="px-2 text-muted-foreground">...</span>
                      )}
                      <Button
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    </div>
                  );
                })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CotizacionesPage;
